// My Generations' rows (issue 238): live keeps a talking head of N actors as one generation of N
// videos, where the engine makes one job per actor with the client's `group` in its params
// (G8-talking-head-video decision 1), simple or Advanced (issue 223) alike. These rules turn the
// list's jobs back into live's rows.
import type { Job, JobStatus } from "../../api/generated/model";
import { isSettled } from "../../platform/jobs/status";

/** One row: a job alone, or every job of one talking head, oldest (the first actor) first. */
export interface Generation {
  key: string;
  jobs: readonly [Job, ...Job[]];
}

/**
 * The list's jobs, newest first, as rows in the same order: the jobs of one `group` in one row at
 * the place of its newest, the rest one each. A group split by a page shows whole once the next
 * page is read.
 */
export function groupGenerations(jobs: readonly Job[]): Generation[] {
  const rows = new Map<string, Job[]>();
  for (const job of jobs) {
    // Each type keeps its group in its own params.
    const key = job.params?.talking_head?.group ?? job.params?.talking_head_advanced?.group ?? job.id;
    const row = rows.get(key);
    if (row === undefined) rows.set(key, [job]);
    else row.push(job);
  }
  return [...rows].map(([key, members]) => {
    const [first, ...rest] = [...members].sort((a, b) => a.created_at.localeCompare(b.created_at));
    return { key, jobs: [first!, ...rest] };
  });
}

/**
 * A row's status: a job still on its way speaks for the row; once all have settled the row is
 * done when any job made its video, as live fails a generation only when it made nothing (its
 * cards show each failed video); otherwise the first job's end.
 */
export function generationStatus(jobs: Generation["jobs"]): JobStatus {
  const going = jobs.find((job) => !isSettled(job.status));
  if (going !== undefined) return going.status;
  if (jobs.some((job) => job.status === "succeeded")) return "succeeded";
  return jobs[0].status;
}
